import { useEffect } from 'react';
import { useSettings } from '@/hooks/useProducts';
import { loadGoogleFont } from '@/lib/fonts';
import { buildThemeCss, themeColorMeta, cacheThemeColor } from '@/theme/apply-theme';

const THEME_STYLE_ID = 'rumio-theme-overrides';

/**
 * Runtime brand application — the pieces of a Branding Studio "Apply to site"
 * (and admin ThemeCustomizer saves) that must be live on EVERY route without a
 * frontend redeploy: the generated theme palette/fonts and the kit favicon.
 *
 * Mounted once in App.tsx above the router. These effects used to live in
 * PublicLayout, which meant AccountLayout, Checkout, and admin routes kept the
 * stale build-time palette and static favicon on cold loads (found on the
 * axiss.support rebrand: /account showed the old green until a PublicLayout
 * route happened to mount first), and SPA navigation out of PublicLayout
 * unmounted the style block entirely.
 */
export default function RuntimeBrand() {
  const { data: settingsData } = useSettings();

  // Runtime favicon override (Branding Studio "Apply to site"): favicon_url
  // points at the applied kit's rendered favicon in tenant storage. The
  // build-time /favicon.svg link stays the fallback — on revert the setting
  // row is deleted and a full reload restores the static icon.
  useEffect(() => {
    const url = settingsData?.settings?.favicon_url;
    if (typeof url !== 'string' || !url) return;
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = url.endsWith('.svg') ? 'image/svg+xml' : 'image/png';
    link.href = url;
  }, [settingsData?.settings?.favicon_url]);

  // Same treatment for the iOS home-screen icon.
  //
  // Safari ignores the web manifest entirely and will not use an SVG, so
  // /apple-touch-icon.png — rasterized at build time from a generated letter
  // tile on most tenants — is the ONLY thing iOS shows. A brand kit renders a
  // proper 180px icon and, until now, applied it nowhere: you could complete a
  // whole brand kit and still have a letter tile on your phone's home screen.
  useEffect(() => {
    const url = settingsData?.settings?.apple_touch_icon_url;
    if (typeof url !== 'string' || !url) return;
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'apple-touch-icon';
      document.head.appendChild(link);
    }
    link.href = url;
  }, [settingsData?.settings?.apple_touch_icon_url]);

  // Apply dynamic theme from CMS. The admin's three base colors are expanded
  // server-side into the full palette (theme_config.generated) plus a
  // complementary dark palette (theme_config.generated.dark). We inject these
  // as a <style> block — `:root` for light, `:root.dark` for dark — rather than
  // inline `documentElement.style`, because inline styles on <html> outrank the
  // `.dark` stylesheet rules and would clobber dark mode. This makes a saved
  // color repaint the whole site AND lets the dark-mode toggle swap palettes.
  useEffect(() => {
    const themeConfig = settingsData?.settings?.theme_config;
    if (!themeConfig) return;

    // ThemeCustomizer stores bare font family names (e.g. 'Inter'); wrap with
    // fallbacks before pushing onto the CSS vars Tailwind reads via
    // font-sans / font-display utilities. loadGoogleFont is a no-op for system
    // fonts (Georgia, system-ui), so we never request those from Google Fonts.
    const fontVars: Record<string, string> = {};
    if (themeConfig.font_body) {
      loadGoogleFont(themeConfig.font_body);
      fontVars['--font-sans'] = `'${themeConfig.font_body}', system-ui, sans-serif`;
    }
    if (themeConfig.font_heading) {
      loadGoogleFont(themeConfig.font_heading);
      fontVars['--font-display'] = `'${themeConfig.font_heading}', Georgia, serif`;
    }

    const css = buildThemeCss(themeConfig, fontVars);
    let styleEl: HTMLStyleElement | null = null;
    if (css) {
      styleEl = document.getElementById(THEME_STYLE_ID) as HTMLStyleElement | null;
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = THEME_STYLE_ID;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = css;
    }

    // Keep the mobile-browser chrome (Chrome address bar, Safari status bar) in
    // sync with the CMS-overridden primary color. Build-time substitution in
    // vite.config.ts handles the baseline; this covers admin live-edits.
    const metaColor = themeColorMeta(themeConfig);
    if (metaColor) {
      const themeMeta = document.querySelector('meta[name="theme-color"]');
      if (themeMeta) themeMeta.setAttribute('content', metaColor);
      // Cache the resolved color so public/theme-boot.js can apply it before
      // first paint on the next load — eliminating the brief flash of the
      // build-time meta value while this effect waits on the settings request.
      cacheThemeColor(metaColor);
    }

    return () => {
      document.getElementById(THEME_STYLE_ID)?.remove();
    };
  }, [settingsData]);

  return null;
}
